// To see this message, add the following to the `<head>` section in your
// views/layouts/application.html.erb
//
//    <%= vite_client_tag %>
//    <%= vite_javascript_tag 'application' %>
console.log("Vite ⚡️ Rails");

// If using a TypeScript entrypoint file:
//     <%= vite_typescript_tag 'application' %>
//
// If you want to use .jsx or .tsx, add the extension:
//     <%= vite_javascript_tag 'application.jsx' %>

// grep-invisible dependencies:
//   sortablejs - installed by our root declaration in package.json, consumed only via
//   @vueuse/integrations/useSortable (optional peer; do not remove on a zero-imports grep).

// console.log(
//   "Visit the guide for more information: ",
//   "https://vite-ruby.netlify.app/guide/rails"
// );

import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "@/entrypoints/App.vue";
import router from "@/router";
import { installOverlayLadder } from "@/composables/overlay/overlayKinds";
import { FLOATING_VUE_THEMES } from "@/composables/overlay/floatingVueThemes";
import { installFloatingVueObserver } from "@/composables/overlay/floatingVueObserver";
import FloatingVue from "floating-vue";
import KeyboardShortcuts from "vue-three-shortkey";
import VueObserveVisibilityPlugin from "vue-observe-visibility";
import { filters } from "@/assets/vueFilters";
import { GLOBAL_HOTKEY_AVOID_SELECTORS } from "@/constants/keyboardOwnership";
import { installPhysicalKeyChordIdentity } from "@/constants/keyboardChordIdentity";
import { pushErrorDiagnostic } from "@/services/clientDiagnostics";

import "@/assets/base.css";
import "floating-vue/dist/style.css";
// MUST stay on the line after the vendor sheet. The bridge overrides
// `.v-popper__popper`'s z at floating-vue's own specificity, so source order is
// the only thing that decides the winner. See the sheet's header and
// `composables/overlay/__tests__/floatingVueBridge.ratchet.test.js`.
import "@/assets/floatingVueBridge.css";

document.addEventListener("DOMContentLoaded", () => {
  const el = document.body.appendChild(document.createElement("div"));
  const app = createApp(App);
  const pinia = createPinia();

  app.use(pinia);
  app.use(router);
  // The theme declarations live in `@/composables/overlay/floatingVueThemes`,
  // not inline here, because the overlay bridge has to read them: which app
  // themes are MENUS — and therefore obstacles other overlays place themselves
  // clear of — is derived from each theme's `$extend` chain, so a fifth theme
  // is one entry there and nothing else anywhere.
  app.use(FloatingVue, { themes: FLOATING_VUE_THEMES });
  // Publishes the overlay ladder's tooltip rung to `:root` as
  // `--th-overlay-z-tooltip`, which is how the floating-vue bridge stylesheet
  // seats `.v-popper__popper` on the ladder without duplicating the number. See
  // `@/composables/overlay/overlayKinds` — the roster is the one author of every
  // stacking band, and this is its only DOM write.
  installOverlayLadder();
  // The ONE place a global hotkey is told to stand down. See
  // `@/constants/keyboardOwnership` — the list is not inlined here because it
  // is a contract surfaces opt into, not a setting of this file.
  app.use(KeyboardShortcuts, { prevent: [...GLOBAL_HOTKEY_AVOID_SELECTORS] });
  // An Alt chord is identified by the PHYSICAL key, not the glyph a modifier
  // rewrote the letter into — without this, Ctrl + Alt + F/S/T reach the
  // interceptor as `ƒ`/`ß`/`†` on every Mac and match nothing. See
  // `@/constants/keyboardChordIdentity` for the rule and why it wraps the
  // plugin's own decoder rather than adding a listener.
  installPhysicalKeyChordIdentity(KeyboardShortcuts);
  app.use(VueObserveVisibilityPlugin);

  app.directive("focus", {
    mounted: (el, binding) => {
      if (binding.value === false) return;
      el.focus();
    },
  });

  // Global diagnostics capture (artemis-product-help D11). Three one-line
  // registrations and no logic of its own: the message + top-frame
  // extraction, truncation and ring bookkeeping all live in
  // `clientDiagnostics.js` (unit-tested there), because this file is a boot
  // script vitest cannot mount. Each handler calls through to the browser's
  // own default behavior (console logging) rather than replacing it, and
  // none may throw or call `preventDefault` -- a diagnostics failure must
  // never be worse than no diagnostics at all.
  app.config.errorHandler = (err, instance, info) => {
    // Assigning a custom errorHandler replaces Vue's default console
    // logging, so log explicitly to preserve today's visibility.
    console.error("Vue error:", err, info);
    pushErrorDiagnostic("uncaught", err);
  };
  window.addEventListener("error", (event) => {
    pushErrorDiagnostic("uncaught", event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    pushErrorDiagnostic("unhandled_rejection", event.reason);
  });

  app.config.globalProperties.$filters = { ...filters };

  // The app's context for a component mounted outside the app's tree (a map
  // marker's avatar, the map glance card): its one reader is
  // `composables/useAppContextMount.js`, the fallback for a caller with no
  // instance, so every directive, plugin and injection resolves there.
  window.__VUE_APP_CONTEXT__ = app._context;

  app.mount(el);

  // Registers every shown dropdown- or menu-themed floating-vue popper in the
  // overlay ledger, so a panel opening beside an open menu is placed clear of
  // it. It observes `document.body`, not the app, which is why it is called
  // after the mount rather than beside the plugin: what it needs is a DOM.
  // Hover tooltips are deliberately never registered — see the module header.
  installFloatingVueObserver();
});
